<script setup lang="ts">
import { computed, ref, watch } from "vue"
import type { UiOption } from "@/types/ui"
import UiFieldWrapper from "./UiFieldWrapper.vue"

const model = defineModel<string | number | null>({ default: null })
const emit = defineEmits<{
  search: [query: string]
}>()

const props = withDefaults(
  defineProps<{
    id: string
    label: string
    options: UiOption[]
    placeholder?: string
    required?: boolean
    disabled?: boolean
    error?: string
    helperText?: string
    minChars?: number
  }>(),
  {
    placeholder: "Digite para buscar...",
    required: false,
    disabled: false,
    error: "",
    helperText: "",
    minChars: 0,
  }
)

const isOpen = ref(false)
const query = ref("")

const selectedOption = computed(() => props.options.find((option) => option.value === model.value) ?? null)

watch(
  () => model.value,
  () => {
    query.value = selectedOption.value?.label ?? ""
  },
  { immediate: true }
)

watch(
  () => query.value,
  (value) => {
    emit("search", value)
  }
)

const filteredOptions = computed(() => {
  const loweredQuery = query.value.toLowerCase().trim()

  if (loweredQuery.length < props.minChars) return []
  if (!loweredQuery) return props.options

  return props.options.filter((option) => option.label.toLowerCase().includes(loweredQuery))
})

function selectOption(option: UiOption) {
  model.value = option.value
  query.value = option.label
  isOpen.value = false
}

function clearSelection() {
  model.value = null
  query.value = ""
  isOpen.value = true
}

function onBlur() {
  window.setTimeout(() => {
    isOpen.value = false
    if (!selectedOption.value) query.value = ""
  }, 100)
}
</script>

<template>
  <UiFieldWrapper :label="label" :for-id="id" :error="error" :helper-text="helperText">
    <div class="relative">
      <input
        :id="id"
        v-model="query"
        type="text"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        autocomplete="off"
        class="w-full rounded-md border border-slate-300 px-3 py-2 pr-10 text-sm text-slate-900 transition placeholder:text-slate-400 focus:border-slate-400 focus:outline-none"
        @focus="isOpen = true"
        @blur="onBlur"
      />

      <button
        v-if="query"
        type="button"
        class="absolute right-2 top-1/2 -translate-y-1/2 rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-100"
        @click="clearSelection"
      >
        limpar
      </button>

      <ul
        v-if="isOpen"
        class="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-slate-200 bg-white p-1 shadow-lg"
      >
        <li v-if="filteredOptions.length === 0" class="px-3 py-2 text-sm text-slate-500">Nenhum resultado</li>

        <li v-for="option in filteredOptions" :key="String(option.value)">
          <button
            type="button"
            class="w-full rounded px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-100"
            @click="selectOption(option)"
          >
            {{ option.label }}
          </button>
        </li>
      </ul>
    </div>
  </UiFieldWrapper>
</template>

